import { Record } from "immutable";
import moment from "moment";
import { formatMessage } from "lib/globalize-util";
import { FLOW_TYPE } from "constants/order-types";
import { IPromotionsData } from "constants/promotion-types";
import { getCurrencySymbol } from "./helpers.js";
import { IAddress } from "components/contacts/contacts-types";

const defaults = {
    _id: undefined,
    currencySymbol: "",
    currency_code: "",
    msrp_currency_code: undefined,
    total: 0,
    line_items: [],
    line_items_count: 0,
    order_flow_type: undefined,
    retailer: undefined,
    order_number: undefined,
    status: undefined,
    draft_name: undefined,
    draft_type: undefined,
    original_order_id: undefined,
    selected_warehouse: undefined,
    __uninitiated_order: false,
    __build_order: undefined,
    order_group_id: undefined,
    order_type: "",
    selected_shipping_locations: [],
    __split_overrides: [],
    __pricing_template: undefined,
};

interface Location {
    _id: string;
    location: string;
}

export interface IShipDate {
    ship_start: string;
    ship_end: string;
    line_items: string[];
}

export enum OrderType {
    Immediate = "immediate",
    Prebook = "prebook",
}

export interface IBuildOrder {
    order_type: OrderType;
    ship_dates: IShipDate[];
}

export interface IRetailer {
    buyer?: string;
    buyer_name?: string;
    ref?: string;
    retailer_name?: string;
}

export interface IOrder {
    _id: string;
    order_flow_type: FLOW_TYPE;
    currencySymbol: string;
    currency_code: string;
    msrp_currency_code?: string;
    line_items: string[];
    line_items_count: number;
    total: number;
    retailer?: IRetailer;
    order_number?: string;
    draft_name?: string;
    draft_type?: string;
    original_order_id?: string;
    status: string;
    promotions?: IPromotionsData;
    selected_warehouse?: string;
    __uninitiated_order?: boolean;
    __build_order?: IBuildOrder;
    order_group_id?: string;
    discount?: number;
    order_type?: string;
    selected_shipping_locations?: Location[];
    __split_overrides?: any[];
    __pricing_template?: string;
    shipping_address?: IAddress;
    is_drop_ship?: boolean;
}

export class OrderRecord extends Record(defaults) {
    // js rewritten, saved for backward
    declare _id: string;
    declare order_flow_type: FLOW_TYPE;
    declare currencySymbol: string;
    declare currency_code: string;
    declare msrp_currency_code?: string;
    declare line_items: string[];
    declare line_items_count: number;
    declare total: number;
    declare retailer?: IRetailer;
    declare order_number?: string;
    declare draft_name?: string;
    declare draft_type?: string;
    declare original_order_id?: string;
    declare status: string;
    declare selected_warehouse: string;
    declare __uninitiated_order?: boolean;
    declare __build_order?: IBuildOrder;
    declare order_group_id: string;
    declare order_type?: string;
    declare selected_shipping_locations?: Location[];
    declare __split_overrides?: any[];
    // TODO: This a temporary solution until WO company fetch request is not moved to the redux!
    // Should be removed when [NU-64106] will be complete
    declare __pricing_template?: string;

    constructor(args) {
        super({
            ...args,
            currencySymbol: getCurrencySymbol(args),
            line_items_count: args.line_items_count,
            total: args.total,
            __build_order: {
                ...args.__build_order,
                ship_dates: args.__build_order?.ship_dates.map((shipDate) => {
                    return {
                        ...shipDate,
                        ship_start: moment.utc(shipDate.ship_start).format("YYYY/MM/DD"),
                        ship_end: moment.utc(shipDate.ship_end).format("YYYY/MM/DD"),
                    };
                }),
            },
        });
    }

    get _mode() {
        switch (this.order_flow_type) {
            case FLOW_TYPE.immediate:
                return formatMessage("Immediate");
            case FLOW_TYPE.prebook:
                return formatMessage("Pre-Book");
            default:
                return "";
        }
    }

    get _totalLabel() {
        const total = this.total > 0 ? this.total : 0;

        let formattedTotal;
        if (global.Intl && global.Intl.NumberFormat) {
            const formatter = new Intl.NumberFormat("en-US", {
                style: "decimal",
                minimumFractionDigits: 2,
            });
            formattedTotal = formatter.format(total);
        } else {
            formattedTotal = total.toFixed(2);
        }

        if (this.currencySymbol) {
            return `${this.currencySymbol} ${formattedTotal}`;
        }
        return formattedTotal;
    }

    isDraft() {
        return this.draft_type === "draft";
    }

    isNotSubmittedOrder() {
        return !this.original_order_id;
    }

    isSubmittedOrder() {
        return !this.isDraft() && this.original_order_id;
    }

    hasRetailer() {
        return this.retailer && this.retailer.ref;
    }

    isInitialized() {
        return this.isSubmittedOrder() || this.hasRetailer() || this.line_items_count > 0;
    }

    isUninitialized() {
        if (this.isSubmittedOrder()) {
            return false;
        } else {
            return !this.hasRetailer() && this.line_items_count === 0;
        }
    }
}
